import { deleteData, getData, patchData, postData } from 'apis/index';
import { API_BASE, CREDENTIALS, POST_HEADERS } from 'constants/apis';
import { RootPathTypes } from 'types/app';
import { BackendCompanyTypes, CompanyUpdateParamsTypes } from 'types/backend/company';
import {
  ComparisonSurveyDataParams,
  ComparisonSurveyStatusParams,
} from 'types/backend/engagementSurvey/comparison';
import {
  QuestionCategoryTypes,
  SurveyIdTypes,
  SurveyInputTypes,
} from 'types/backend/engagementSurvey/result';
import {
  SegmentGroupInputTypes,
  SegmentGroupSortParamsTypes,
  SegmentGroupUpdateTypes,
} from 'types/backend/segment';
import { UserInputTypes } from 'types/backend/user';
import { CompanyTypes } from 'types/backyard/company';

// URLSearchParams for IE
import 'core-js/features/url-search-params';

const urlSearchParams = new URLSearchParams(window.location.search);

// backend/user
export const requestVerifySession = async (rootPath: RootPathTypes) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/users/verify_session`
      : `${API_BASE}/backyard_users/verify_session`;
  const response = await getData(url);
  return response;
};

export const requestSignIn = async (
  rootPath: RootPathTypes,
  signInEmail: string,
  signInPassword: string,
  isSessionExtended: boolean,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin' ? `${API_BASE}/users/sign_in` : `${API_BASE}/backyard_users/sign_in`;
  let body = `email=${encodeURIComponent(
    signInEmail,
  )}&password=${encodeURIComponent(signInPassword)}`;
  if (isSessionExtended) body += '&remember_me=true';
  const response = await postData(url, body, xCSRFToken);
  return response;
};

export const requestSignOut = async (rootPath: RootPathTypes) => {
  const url =
    rootPath === 'admin' ? `${API_BASE}/users/sign_out` : `${API_BASE}/backyard_users/sign_out`;
  // sign_outはresponseが無いため、statusを返す
  try {
    const res = await fetch(url, { credentials: CREDENTIALS });
    return {
      payload: { status: res.status },
      newXCSRFToken: res.headers.get('x-csrf-token'),
    };
  } catch (error) {
    return { error };
  }
};

export const requestResetPassword = async (
  rootPath: RootPathTypes,
  passwordResetEmail: string,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin' ? `${API_BASE}/users/password` : `${API_BASE}/backyard_users/password`;
  const body = `email=${encodeURIComponent(passwordResetEmail)}`;

  // responseが無いため、statusを返す
  try {
    const res = await fetch(url, {
      credentials: CREDENTIALS,
      method: 'POST',
      headers: POST_HEADERS(xCSRFToken),
      body,
    });
    return { payload: { status: res.status } };
  } catch (error) {
    return { error };
  }
};

export const requestUpdatePassword = async (
  rootPath: RootPathTypes,
  password: string,
  passwordConfirmation: string,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin' ? `${API_BASE}/users/password` : `${API_BASE}/backyard_users/password`;
  const resetPasswordToken = urlSearchParams.get('reset_password_token');
  let body = `password=${encodeURIComponent(
    password,
  )}&password_confirmation=${encodeURIComponent(passwordConfirmation)}`;
  if (resetPasswordToken) body += `&reset_password_token=${resetPasswordToken}`;
  const response = await patchData(url, body, xCSRFToken);
  return response;
};

export const requestInitializeConfirmation = async (
  rootPath: RootPathTypes,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/users/confirmation`
      : `${API_BASE}/backyard_users/confirmation`;
  const confirmationToken = urlSearchParams.get('confirmation_token');
  const body = `confirmation_token=${confirmationToken}`;
  const response = await patchData(url, body, xCSRFToken);
  return response;
};

export const requestInitializeReconfirmation = async (
  rootPath: RootPathTypes,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/users/confirmation`
      : `${API_BASE}/backyard_users/confirmation`;
  const confirmationToken = urlSearchParams.get('confirmation_token');
  const body = `confirmation_token=${confirmationToken}`;
  const response = await patchData(url, body, xCSRFToken);
  return response;
};

export const requestRegister = async (
  rootPath: RootPathTypes,
  password: string,
  passwordConfirmation: string,
  isAgreedPrivacyPolicy: string,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/users/registration`
      : `${API_BASE}/backyard_users/registration`;
  const confirmationToken = urlSearchParams.get('confirmation_token');
  let body = `password=${encodeURIComponent(
    password,
  )}&password_confirmation=${encodeURIComponent(passwordConfirmation)}`;
  if (isAgreedPrivacyPolicy) body += `&privacy_policy_agreed=${true}`;
  if (confirmationToken) body += `&confirmation_token=${confirmationToken}`;
  const response = await patchData(url, body, xCSRFToken);
  return response;
};

// backend/engagementSurvey/survey
export const requestFetchSurveys = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys`;
  const response = await getData(url);
  return response;
};

export const requestCreateSurvey = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  params: SurveyInputTypes,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys`;
  let body = `start_at=${encodeURIComponent(
    String(params.startAt),
  )}&end_at=${encodeURIComponent(String(params.endAt))}`;
  if (params.targetNumber) body += `&target_number=${encodeURIComponent(params.targetNumber)}`;
  if (params.note) body += `&note=${encodeURIComponent(params.note)}`;
  const response = await postData(url, body, xCSRFToken);
  return response;
};

export const requestUpdateSurvey = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  params: SurveyInputTypes,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/${params.surveyId}`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/${params.surveyId}`;
  let body = `start_at=${encodeURIComponent(
    String(params.startAt),
  )}&end_at=${encodeURIComponent(String(params.endAt))}`;
  if (params.targetNumber) body += `&target_number=${encodeURIComponent(params.targetNumber)}`;
  if (params.note) body += `&note=${encodeURIComponent(params.note)}`;
  if (typeof params.publish === 'boolean') body += `&publish=${encodeURIComponent(params.publish)}`;
  const response = await patchData(url, body, xCSRFToken);
  return response;
};

export const requestDeleteSurvey = async (
  rootPath: RootPathTypes,
  currentCompany: BackendCompanyTypes,
  id: string,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/${id}`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/${id}`;
  const response = await deleteData(url, xCSRFToken);
  return response;
};

// backend/engagementSurvey/result
export const requestFetchSurveyResultRatioSummary = async (
  rootPath: RootPathTypes,
  surveyId: SurveyIdTypes,
  currentCompany: CompanyTypes,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/${surveyId}/ratio`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/${surveyId}/ratio`;
  const response = await getData(url);
  return response;
};

export const requestFetchSurveyResultRatioDistribution = async (
  rootPath: RootPathTypes,
  surveyId: SurveyIdTypes,
  currentCompany: CompanyTypes,
) => {
  let url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/engagement_scores`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/engagement_scores`;
  if (surveyId) url += `?survey_id=${surveyId}`;
  const response = await getData(url);
  return response;
};

export const requestFetchSurveyResultRatioEngagements = async (
  rootPath: RootPathTypes,
  surveyId: SurveyIdTypes,
  currentCompany: CompanyTypes,
) => {
  let url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/${surveyId}/ratio`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/${surveyId}/ratio`;
  url += '?question_type=engagement';
  const response = await getData(url);
  return response;
};

export const requestFetchSurveyResultRatioTimeSeries = async (
  rootPath: RootPathTypes,
  surveyId: SurveyIdTypes,
  currentCompany: CompanyTypes,
) => {
  let url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/ratios`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/ratios`;
  if (surveyId) url += `?survey_id=${surveyId}`;
  const response = await getData(url);
  return response;
};

export const requestFetchSurveyResultRatioDepartments = async (
  rootPath: RootPathTypes,
  surveyId: SurveyIdTypes,
  parentId: number | null,
  currentCompany: CompanyTypes,
) => {
  let url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/${surveyId}/ratio`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/${surveyId}/ratio`;
  url += '?segment_type=department';
  url += `&parent_department_id=${parentId || ''}`;
  const response = await getData(url);
  return response;
};

export const requestFetchSurveyResultCategorySummary = async (
  rootPath: RootPathTypes,
  surveyId: SurveyIdTypes,
  currentCompany: CompanyTypes,
) => {
  let url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/question_categories`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/question_categories`;
  if (surveyId) url += `?survey_id=${surveyId}`;
  const response = await getData(url);
  return response;
};

export const requestFetchSurveyResultCategoryDetail = async (
  rootPath: RootPathTypes,
  questionCategoryType: QuestionCategoryTypes,
  surveyId: SurveyIdTypes,
  currentCompany: CompanyTypes,
) => {
  let url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/question_categories`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/question_categories`;
  url += `?category_type=${questionCategoryType}&with_children=true`;
  if (surveyId) url += `&survey_id=${surveyId}`;
  const response = await getData(url);
  if (response.payload) return { payload: response.payload.categories[0] };
  return response;
};

// backend/segment
export const requestSortSegmentGroup = async (
  rootPath: RootPathTypes,
  currentCompany: BackendCompanyTypes | null,
  xCSRFToken: string,
  params: SegmentGroupSortParamsTypes,
) => {
  if (rootPath !== 'admin' && !currentCompany)
    return { error: new Error('currentCompany is null') };
  const url =
    rootPath === 'admin' || !currentCompany
      ? `${API_BASE}/admin/segment_groups/${params.targetId}/sort`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/segment_groups/${params.targetId}/sort`;
  let body = '';
  if (params.parentId) body += `&parent_id=${encodeURIComponent(params.parentId)}`;
  if (params.rightItemId) body += `&right_item_id=${encodeURIComponent(params.rightItemId)}`;
  if (params.leftItemId) body += `&left_item_id=${encodeURIComponent(params.leftItemId)}`;
  const response = await patchData(url, body, xCSRFToken);
  return response;
};

export const requestFetchSegmentGroups = async (
  rootPath: RootPathTypes,
  currentCompany: BackendCompanyTypes | null,
) => {
  if (rootPath !== 'admin' && !currentCompany)
    return { error: new Error('currentCompany is null') };
  const url =
    rootPath === 'admin' || !currentCompany
      ? `${API_BASE}/admin/segment_groups`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/segment_groups`;
  const response = await getData(url);
  return response;
};

export const requestCreateSegmentGroup = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  params: SegmentGroupInputTypes,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/segment_groups`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/segment_groups`;
  let body = `segment_type=${encodeURIComponent(params.segmentType)}`;
  body += `&name=${encodeURIComponent(params.name)}`;
  const response = await postData(url, body, xCSRFToken);
  return response;
};

export const requestUpdateSegmentGroup = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  params: SegmentGroupUpdateTypes,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/segment_groups/${params.segmentGroupId}`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/segment_groups/${params.segmentGroupId}`;
  const body = `name=${encodeURIComponent(params.name)}`;
  const response = await patchData(url, body, xCSRFToken);
  return response;
};

export const requestInactivateSegmentGroup = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  surveySegmentId: string,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/segment_groups/${surveySegmentId}/inactive`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/segment_groups/${surveySegmentId}/inactive`;
  const response = await patchData(url, '', xCSRFToken);
  return response;
};

// backend/user
export const requestFetchUsers = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  pageNumber: number,
) => {
  let url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/users?`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/users?`;
  if (pageNumber) url += `&page=${pageNumber}&limit=20`;
  const response = await getData(url);
  return response;
};

export const requestCreateUser = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  params: UserInputTypes,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/users`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/users`;
  let body = `email=${encodeURIComponent(params.email)}`;
  body += `&last_name=${encodeURIComponent(params.lastName)}`;
  body += `&first_name=${encodeURIComponent(params.firstName)}`;
  const response = await postData(url, body, xCSRFToken);
  return response;
};

export const requestUpdateUser = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  params: UserInputTypes,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/users/${params.userId}`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/users/${params.userId}`;
  let body = `email=${encodeURIComponent(params.email)}`;
  body += `&last_name=${encodeURIComponent(params.lastName)}`;
  body += `&first_name=${encodeURIComponent(params.firstName)}`;
  if (params.userStatus) body += `&status=${encodeURIComponent(params.userStatus)}`;
  const response = await patchData(url, body, xCSRFToken);
  return response;
};

export const requestDeleteUser = async (
  rootPath: RootPathTypes,
  currentCompany: BackendCompanyTypes,
  id: string,
  xCSRFToken: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/users/${id}`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/users/${id}`;
  const response = await deleteData(url, xCSRFToken);
  return response;
};

// comparison
export const requestFetchEngagementSurveySegments = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  surveyId: string,
) => {
  const url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/${surveyId}/segments`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/${surveyId}/segments`;
  const response = await getData(url);
  return response;
};

// 比較データリクエスト
export const requestFetchComparisonQuestionCategory = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  params: ComparisonSurveyDataParams,
) => {
  let url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/${params.surveyId}/question_categories?`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/${params.surveyId}/question_categories?`;
  if (params.categoryType) url += `&category_type=${params.categoryType}`;
  if (params.withChildren) url += `&with_children=${params.withChildren}`;
  if (params.segmentGroupDepartmentIds)
    url += `&survey_segment_group_department_ids=${params.segmentGroupDepartmentIds}`;
  if (params.segmentGroupPositionIds)
    url += `&survey_segment_group_position_ids=${params.segmentGroupPositionIds}`;
  if (params.segmentGroupAgeIds)
    url += `&survey_segment_group_age_ids=${params.segmentGroupAgeIds}`;
  if (params.segmentGroupEmploymentTypeIds)
    url += `&survey_segment_group_employment_type_ids=${params.segmentGroupEmploymentTypeIds}`;
  if (params.segmentGroupRecruitmentTypeIds)
    url += `&survey_segment_group_recruitment_type_ids=${params.segmentGroupRecruitmentTypeIds}`;
  if (params.segmentGroupGenderIds)
    url += `&survey_segment_group_gender_ids=${params.segmentGroupGenderIds}`;
  if (params.segmentGroupSeniorityIds)
    url += `&survey_segment_group_seniority_ids=${params.segmentGroupSeniorityIds}`;

  const response = await getData(url);
  return response.payload;
};

// エンゲージメントステータス取得
export const requestFetchComparisonSurveyRatio = async (
  rootPath: RootPathTypes,
  currentCompany: CompanyTypes,
  params: ComparisonSurveyStatusParams,
) => {
  let url =
    rootPath === 'admin'
      ? `${API_BASE}/admin/surveys/${params.surveyId}/ratio?`
      : `${API_BASE}/backyard/companies/${currentCompany.id}/surveys/${params.surveyId}/ratio?`;
  if (params.segmentGroupDepartmentIds)
    url += `&survey_segment_group_department_ids=${params.segmentGroupDepartmentIds}`;
  if (params.segmentGroupPositionIds)
    url += `&survey_segment_group_position_ids=${params.segmentGroupPositionIds}`;
  if (params.segmentGroupAgeIds)
    url += `&survey_segment_group_age_ids=${params.segmentGroupAgeIds}`;
  if (params.segmentGroupEmploymentTypeIds)
    url += `&survey_segment_group_employment_type_ids=${params.segmentGroupEmploymentTypeIds}`;
  if (params.segmentGroupRecruitmentTypeIds)
    url += `&survey_segment_group_recruitment_type_ids=${params.segmentGroupRecruitmentTypeIds}`;
  if (params.segmentGroupGenderIds)
    url += `&survey_segment_group_gender_ids=${params.segmentGroupGenderIds}`;
  if (params.segmentGroupSeniorityIds)
    url += `&survey_segment_group_seniority_ids=${params.segmentGroupSeniorityIds}`;
  const response = await getData(url);
  return response;
};

// backend/company
export const requestFetchCompany = async (rootPath: RootPathTypes, id: string) => {
  const url =
    rootPath === 'admin' ? `${API_BASE}/admin/company` : `${API_BASE}/backyard/companies/${id}`;
  const response = await getData(url);
  return response;
};

export const requestUpdateCompany = async (
  params: CompanyUpdateParamsTypes,
  xCSRFToken: string,
) => {
  const url = `${API_BASE}/backyard/companies/${params.id}`;
  let body = `&name=${encodeURIComponent(params.name)}`;
  body += `&demo=${encodeURIComponent(params.demo)}`;
  body += `&canceled_at=${encodeURIComponent(String(params.cancelDate))}`;
  body += `&deleted_at=${encodeURIComponent(String(params.deleteDate))}`;
  const response = await patchData(url, body, xCSRFToken);
  return response;
};
